import { useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import StoreIcon from "@mui/icons-material/Store";
import ApartmentIcon from "@mui/icons-material/Apartment";
import { Box, Button, Chip, Stack, Typography } from "@mui/material";

import { env } from "../../../app/config/env";
import { useAuth } from "../../../app/providers/AuthProvider";
import { useEmpresa } from "../../../app/providers/EmpresaProvider";
import AuthApi from "../api/authApi";
import AppIcon from "../../../assets/AppIcon.svg";

export default function TerminalSelectBranchPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const appName = env.APP_NAME;

  const { user, loading } = useAuth();
  const {
    empresaId,
    empresaNombre,
    sucursalId,
    setSucursalId,
    setSucursalNombre,
  } = useEmpresa();

  const nextPath = location.state?.next || "/attendance";
  const role = String(user?.user_role || "").toUpperCase();
  const isAllowed = role === "OWNER" || role === "MANAGER";

  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [selectedBranchId, setSelectedBranchId] = useState(sucursalId || "");
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    if (loading) return;

    if (!user) {
      navigate("/login", { replace: true });
      return;
    }

    if (!isAllowed) {
      navigate("/login", { replace: true });
      return;
    }

    if (!empresaId) {
      navigate("/login", { replace: true });
    }
  }, [loading, user, isAllowed, empresaId, navigate]);

  useEffect(() => {
    let active = true;

    async function loadBranches() {
      if (!empresaId) return;

      setLoadingBranches(true);
      setErrorMsg("");

      try {
        const rows = await AuthApi.listBranchesByLicense({ licenseId: empresaId });
        if (!active) return;

        const normalized = (Array.isArray(rows) ? rows : []).map((b) => ({
          id: String(b.id),
          name: String(b.name || `Sucursal #${b.id}`),
          address: String(b.address || ""),
        }));

        setBranches(normalized);

        setSelectedBranchId((prev) => {
          if (!prev && normalized.length === 1) {
            return normalized[0].id;
          }
          return prev;
        });
      } catch (err) {
        if (!active) return;
        setBranches([]);
        setErrorMsg(err?.message || "No se pudieron cargar las sucursales.");
      } finally {
        if (active) setLoadingBranches(false);
      }
    }

    loadBranches();

    return () => {
      active = false;
    };
  }, [empresaId]);

  const selected = useMemo(
    () => branches.find((b) => b.id === selectedBranchId) || null,
    [branches, selectedBranchId]
  );

  function handleIngresar() {
    if (!selected) return;

    const finalBranchId = String(selected.id);
    const finalBranchName = String(selected.name || "");

    setSucursalId(finalBranchId);
    setSucursalNombre(finalBranchName);

    localStorage.setItem("sabi_empresa_id", String(empresaId));
    localStorage.setItem("sabi_empresa_nombre", String(empresaNombre || ""));
    localStorage.setItem("sabi_sucursal_id", finalBranchId);
    localStorage.setItem("sabi_sucursal_nombre", finalBranchName);

    navigate(nextPath, { replace: true });
  }

  if (loading) return null;

  return (
    <Box
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" && selected) {
          e.preventDefault();
          handleIngresar();
        }
      }}
      sx={{
        width: "100vw",
        height: "100vh",
        bgcolor: "rgba(230, 230, 234, 1)",
        display: "grid",
        placeItems: "center",
        overflow: "hidden",
        outline: "none",
      }}
    >
      <Box sx={{ width: "100%", height: "100%", position: "relative" }}>
        <Stack
          direction="row"
          spacing={1.5}
          alignItems="center"
          sx={{ position: "absolute", top: 20, left: 20 }}
        >
          <Box
            component="img"
            src={AppIcon}
            alt={`${appName} icon`}
            sx={{
              width: 48,
              height: 48,
              borderRadius: "50%",
              objectFit: "contain",
              bgcolor: "transparent",
            }}
          />
          <Typography sx={{ fontSize: 18, fontWeight: 600, color: "rgba(2, 8, 23, 1)" }}>
            {appName}
          </Typography>
        </Stack>

        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
          }}
        >
          <Box
            sx={{
              width: "440px",
              maxWidth: "92vw",
              bgcolor: "rgba(191, 198, 225, 1)",
              borderRadius: "30px",
              p: "20px",
              boxShadow:
                "inset 0px 0px 4px rgba(117, 117, 138, 0.5), 0px 0px 10px rgba(165, 165, 178, 0.5)",
            }}
          >
            <Stack spacing={3.75} alignItems="center">
              <Stack spacing={2.5} alignItems="center" sx={{ width: "100%" }}>
                <Box
                  sx={{
                    width: "60px",
                    height: "60px",
                    borderRadius: "42px",
                    bgcolor: "rgba(14, 50, 149, 1)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <ApartmentIcon sx={{ width: "42px", height: "42px", color: "white" }} />
                </Box>

                <Box
                  sx={{
                    px: "20px",
                    py: "10px",
                    bgcolor: "rgba(14, 50, 149, 1)",
                    borderRadius: "33px",
                    boxShadow: "inset 0px 0px 3px rgba(192, 192, 201, 0.5)",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                      fontSize: "22px",
                      fontWeight: 600,
                      color: "rgba(254, 254, 255, 1)",
                    }}
                  >
                    Sucursal
                  </Typography>
                </Box>
              </Stack>

              <Stack spacing={2.5} alignItems="center" sx={{ width: "100%" }}>
                <Typography
                  sx={{
                    fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                    fontSize: "18px",
                    fontWeight: 600,
                    color: "rgba(2, 8, 23, 1)",
                    textAlign: "center",
                  }}
                >
                  Elegí la sucursal de la terminal:
                </Typography>

                {empresaNombre ? (
                  <Typography
                    sx={{
                      fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                      fontSize: "14px",
                      fontWeight: 600,
                      color: "rgba(2, 8, 23, 0.7)",
                      textAlign: "center",
                    }}
                  >
                    Empresa seleccionada: {empresaNombre}
                  </Typography>
                ) : null}

                <Stack
                  spacing="10px"
                  alignItems="center"
                  sx={{
                    width: "100%",
                    bgcolor: "rgba(247, 247, 248, 1)",
                    borderRadius: "20px",
                    boxShadow: "inset 0px 0px 2px rgba(96, 96, 113, 0.5)",
                    px: "10px",
                    py: "20px",
                  }}
                >
                  {loadingBranches ? (
                    <Typography sx={{ fontSize: 14, color: "rgba(2,8,23,.7)" }}>
                      Cargando sucursales...
                    </Typography>
                  ) : errorMsg ? (
                    <Typography sx={{ fontSize: 14, color: "rgba(180,30,30,1)", textAlign: "center" }}>
                      {errorMsg}
                    </Typography>
                  ) : branches.length === 0 ? (
                    <Typography sx={{ fontSize: 14, color: "rgba(2,8,23,.7)" }}>
                      No hay sucursales disponibles para esta empresa.
                    </Typography>
                  ) : (
                    branches.map((b) => {
                      const isSel = selectedBranchId === b.id;
                      return (
                        <Chip
                          key={b.id}
                          icon={<StoreIcon sx={{ width: 24, height: 24, color: "rgba(2, 8, 23, 1)" }} />}
                          label={
                            <Box sx={{ py: 0.25 }}>
                              <Typography
                                sx={{
                                  fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                                  fontSize: 16,
                                  fontWeight: 600,
                                  color: "rgba(2, 8, 23, 1)",
                                  textAlign: "left",
                                }}
                              >
                                {b.name}
                              </Typography>
                              {b.address ? (
                                <Typography
                                  sx={{
                                    fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                                    fontSize: 12,
                                    color: "rgba(2, 8, 23, 0.7)",
                                    textAlign: "left",
                                  }}
                                >
                                  {b.address}
                                </Typography>
                              ) : null}
                            </Box>
                          }
                          onClick={() => setSelectedBranchId(b.id)}
                          sx={{
                            width: "100%",
                            maxWidth: "360px",
                            justifyContent: "flex-start",
                            bgcolor: isSel ? "rgba(191, 198, 225, 1)" : "rgba(216, 220, 237, 1)",
                            borderRadius: "33px",
                            px: "10px",
                            py: "10px",
                            height: "auto",
                            boxShadow: "inset 0px 0px 3px rgba(142, 142, 159, 0.5)",
                            cursor: "pointer",
                            "&:hover": { bgcolor: "rgba(191, 198, 225, 1)" },
                            "& .MuiChip-icon": { marginLeft: 0 },
                            "& .MuiChip-label": {
                              display: "block",
                              width: "100%",
                              whiteSpace: "normal",
                              py: "6px",
                            },
                          }}
                        />
                      );
                    })
                  )}
                </Stack>
              </Stack>

              <Button
                disabled={!selected}
                onClick={handleIngresar}
                sx={{
                  px: "20px",
                  py: "10px",
                  bgcolor: "rgba(18, 64, 191, 1)",
                  borderRadius: "33px",
                  textTransform: "none",
                  "&:hover": { bgcolor: "rgba(14, 50, 149, 1)" },
                  "&.Mui-disabled": {
                    bgcolor: "rgba(18, 64, 191, 0.35)",
                    color: "rgba(255,255,255,0.9)",
                  },
                }}
              >
                <Typography
                  sx={{
                    fontFamily: '"Open Sans", Helvetica, Arial, sans-serif',
                    fontSize: "16px",
                    fontWeight: 700,
                    color: "rgba(254, 254, 255, 1)",
                  }}
                >
                  Ingresar
                </Typography>
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}